import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type { AuthSchemeWithSecret } from 'api';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { CopyIcon } from '@atoms/Icon/CopyIcon';

import {
  CREATE_KEY_REVEAL_DONE_LABEL,
  CREATE_KEY_REVEAL_DONE_TEST_ID,
  CREATE_KEY_REVEAL_SECRET_COPY_TEST_ID,
  CREATE_KEY_REVEAL_SECRET_LABEL,
  CREATE_KEY_REVEAL_SECRET_TEST_ID,
  CREATE_KEY_REVEAL_SECRET_WARNING,
  CREATE_KEY_REVEAL_TEST_ID,
  CREATE_KEY_REVEAL_UUID_COPY_TEST_ID,
  CREATE_KEY_REVEAL_UUID_LABEL,
  CREATE_KEY_REVEAL_UUID_TEST_ID
} from './constants';

type KeyCreatedPanelProps = {
  scheme: AuthSchemeWithSecret;
  onDone: () => void;
};

type RevealChipProps = {
  label: string;
  value: string;
  valueTestId: string;
  copyTestId: string;
  onCopy: () => void;
};

const RevealChip = ({
  label,
  value,
  valueTestId,
  copyTestId,
  onCopy
}: RevealChipProps) => (
  <div className="flex flex-col gap-2">
    <p className="text-xs font-semibold leading-[14px] text-content-primary">
      {label}
    </p>
    <div className="flex items-center justify-between gap-2 rounded-lg bg-surface-muted p-2">
      <span
        data-testid={valueTestId}
        className="flex-1 break-all font-mono text-xs leading-[22px] tracking-[0.36px] text-content-primary"
      >
        {value}
      </span>
      <CopyToClipboard text={value} onCopy={onCopy}>
        <button
          type="button"
          data-testid={copyTestId}
          aria-label={`Copy ${label}`}
          className="cursor-pointer text-content-secondary hover:text-content-primary"
        >
          <CopyIcon width={20} height={20} stroke="auto" className="shrink-0" />
        </button>
      </CopyToClipboard>
    </div>
  </div>
);

export const KeyCreatedPanel = ({ scheme, onDone }: KeyCreatedPanelProps) => {
  const openNotification = useOpenNotification();

  const onCopySuccess = (label: string) =>
    openNotification({
      kind: 'success',
      message: `${label} copied to your clipboard`
    });

  return (
    <div
      data-testid={CREATE_KEY_REVEAL_TEST_ID}
      className="flex flex-col gap-4"
    >
      <RevealChip
        label={CREATE_KEY_REVEAL_UUID_LABEL}
        value={scheme.uuid}
        valueTestId={CREATE_KEY_REVEAL_UUID_TEST_ID}
        copyTestId={CREATE_KEY_REVEAL_UUID_COPY_TEST_ID}
        onCopy={() => onCopySuccess(CREATE_KEY_REVEAL_UUID_LABEL)}
      />

      {scheme.secret && (
        <div className="flex flex-col gap-2">
          <RevealChip
            label={CREATE_KEY_REVEAL_SECRET_LABEL}
            value={scheme.secret}
            valueTestId={CREATE_KEY_REVEAL_SECRET_TEST_ID}
            copyTestId={CREATE_KEY_REVEAL_SECRET_COPY_TEST_ID}
            onCopy={() => onCopySuccess(CREATE_KEY_REVEAL_SECRET_LABEL)}
          />
          <div className="flex items-start gap-2 rounded-md bg-feedback-warning-surface p-3 text-sm text-content-primary">
            <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0 text-feedback-attention" />
            <span>{CREATE_KEY_REVEAL_SECRET_WARNING}</span>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <PrimaryButton
          data-testid={CREATE_KEY_REVEAL_DONE_TEST_ID}
          onClick={onDone}
        >
          {CREATE_KEY_REVEAL_DONE_LABEL}
        </PrimaryButton>
      </div>
    </div>
  );
};
